import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import type { PlatformResponseDTO } from '@libs/dtos/platform/platform.dto';
import { ProductResponseDTO } from '@libs/dtos/product/product.dto';
import { ViewportService } from '@services/geo/viewport.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { PlatformService } from '@services/platforms/platform.service';
import { ProductService } from '@services/products/product.service';
import { ConfirmDialogService } from '@services/ui/confirm-dialog.service';
import { TableBase } from '@shared/bases/table.base';
import { ButtonComponent } from '@shared/components/button/button.component';
import { TableComponent } from '@shared/components/table/table.component';
import { TableAction } from '@shared/components/table/table.model';
import { SharedModule } from '@shared/modules/shared.module';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { ProductCrudComponent } from './product-crud/product-crud.component';
import { ProductOrderComponent } from './product-order/product-order.component';

interface ProductTableData extends Omit<ProductResponseDTO, 'amounts' | 'prices'> {
	amounts: string;
	prices: string;
	platform?: string;
	activeProviderDisplay?: string;
}

/**
 * Table row for a product: amounts/prices as text, platform name (or "Plataforma <id>" when the
 * platform list is unavailable) and the active provider with its code.
 */
export function toProductRow(
	product: ProductResponseDTO,
	platforms: PlatformResponseDTO[] | null,
	t: (key: string, params?: Record<string, unknown>) => string,
): ProductTableData {
	const activeProvider = product.activeProvider || product.providers?.find((p) => p.isActive);
	const activeProviderDisplay = activeProvider
		? `${activeProvider.providerName || t('productsCrud.messages.providerFallback')} (${activeProvider.code})`
		: t('productsCrud.messages.noProviderDisplay');
	const platform = platforms
		? platforms.find((p) => p.id.toString() === product.platformId.toString())?.name ||
			t('productsCrud.messages.unknownPlatform')
		: t('productsCrud.messages.platformFallback', { id: product.platformId });

	return {
		...product,
		amounts: product.amounts.join(', '),
		prices: product.prices.join(', '),
		platform,
		activeProviderDisplay,
	} as ProductTableData;
}

@Component({
	selector: 'app-products',
	imports: [SharedModule, TableComponent, ButtonComponent, ProductCrudComponent, ProductOrderComponent],
	templateUrl: './product.component.html',
	styleUrl: './product.component.scss',
})
export class ProductComponent extends TableBase implements OnInit {
	@ViewChild('crudProduct') crudProduct!: ProductCrudComponent;

	data: ProductTableData[] = [];
	platforms: { label: string; value: string }[] = [];

	// Column headers
	headers: any[] = [];

	// ====== Pagination Configuration ======
	override mainField = 'name';
	override sortField = 'platformId';
	override limit = 10;
	loadError = false;

	constructor(
		private router: Router,
		private productService: ProductService,
		private platformService: PlatformService,
		private messageAlertService: MessageAlertService,
		private confirmDialogService: ConfirmDialogService,
		private cdr: ChangeDetectorRef,
		override viewportService: ViewportService,
		private translocoService: TranslocoService,
	) {
		super(viewportService);
		this.headers = [
			{ field: 'name', header: this.translocoService.translate('productsCrud.headers.product') },
			{
				field: 'activeProviderDisplay',
				header: this.translocoService.translate('productsCrud.headers.activeProvider'),
				sortable: false, // display-only column, not a database field
			},
			{
				field: 'amounts',
				header: this.translocoService.translate('productsCrud.headers.amounts'),
				sortable: false,
			},
			{
				field: 'prices',
				header: this.translocoService.translate('productsCrud.headers.prices'),
				sortable: false,
			},
			{
				field: 'platform',
				header: this.translocoService.translate('productsCrud.headers.platform'),
				sortField: 'platformId',
			},
		];
	}

	override loadData(): void {
		this.loadProducts();
	}

	ngOnInit(): void {
		this.limit = this.isMobile ? this.DEFAULT_LIMIT_MOBILE : this.DEFAULT_LIMIT;
		this.isLoading = true; // Show skeleton immediately
		this.loadPlatforms();
		this.loadProducts();
	}

	/**
	 * Loads platforms for the filter dropdown
	 */
	loadPlatforms(): void {
		this.platformService.getPlatformOptions().subscribe({
			next: (options) => {
				this.platforms = options;
				this.filterFields = [
					{
						key: 'platformId',
						label: this.translocoService.translate('productsCrud.filter.platform'),
						type: 'select' as const,
						options: this.platforms,
						placeholder: this.translocoService.translate('productsCrud.filter.allPlatforms'),
					},
				];
				this.cdr.detectChanges();
			},
			// The list still works without the platform filter; the user is told why it is missing
			error: () =>
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('common.states.platformsLoadError'),
				),
		});
	}

	/**
	 * After the form saves: an edit replaces its row (the update answers with the full product); a new
	 * product, or one moved to another platform (the list is sorted/filtered by platform), reloads.
	 */
	onProductSaved(event: { product: ProductResponseDTO; created: boolean }): void {
		const current = this.data.find((p) => p.id === event.product.id);
		if (event.created || !current || current.platformId.toString() !== event.product.platformId.toString()) {
			this.loadProducts();
			return;
		}
		this.platformService
			.getAllPlatforms()
			.pipe(catchError(() => of(null)))
			.subscribe((platforms) => {
				const row = toProductRow(event.product, platforms, (key, params) =>
					this.translocoService.translate(key, params),
				);
				this.data = this.data.map((p) => (p.id === row.id ? row : p));
				this.cdr.detectChanges();
			});
	}

	/**
	 * Fetches products with pagination and sorting options. Platform names come from the shared
	 * catalog (one request per session); if it fails the rows show "Plataforma <id>".
	 */
	loadProducts(): void {
		this.isLoading = true;
		this.loadError = false;
		this.cdr.detectChanges(); // Force immediate skeleton display

		const params: any = {
			page: this.page,
			limit: this.limit,
			sort_by: this.sortField,
			sort_order: this.sortOrder === 1 ? 'asc' : 'desc',
		};

		// Add platform filter if selected
		if (this.currentFilters.platformId) {
			params.platformId = this.currentFilters.platformId;
		}

		this.productService
			.getProducts(params)
			.pipe(
				switchMap((response) =>
					this.platformService.getAllPlatforms().pipe(
						catchError(() => of(null)),
						map((platforms) => ({ response, platforms })),
					),
				),
			)
			.subscribe({
				next: ({ response, platforms }) => {
					const t = (key: string, params?: Record<string, unknown>) =>
						this.translocoService.translate(key, params);
					this.data = (response?.data ?? []).map((product) => toProductRow(product, platforms, t));
					this.totalRecords = response?.total ?? 0;
					this.isLoading = false;
					this.cdr.detectChanges();
				},
				error: () => {
					this.data = [];
					this.totalRecords = 0;
					this.isLoading = false;
					this.loadError = true;
					this.cdr.detectChanges();
				},
			});
	}

	/**
	 * Navigates to the product detail page.
	 */
	onViewDetail(product: ProductTableData): void {
		if (!product?.id) {
			console.error('No product ID provided for detail view');
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('productsCrud.messages.noValidProductIdView'),
			);
			return;
		}

		this.router.navigate(['/products', product.id]);
	}

	/**
	 * Opens the product form in update mode.
	 */
	onUpdateProduct(product: ProductTableData): void {
		if (!product?.id) {
			console.error('No product ID provided for update');
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('productsCrud.messages.noValidProductIdEdit'),
			);
			return;
		}

		if (!this.crudProduct) {
			console.error('CrudProduct component not found');
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('productsCrud.messages.editNotAvailable'),
			);
			return;
		}

		// The table row is formatted for display (prices joined as text, labels instead of ids):
		// the form edits the persisted product, so it is fetched as it is saved
		this.productService.getProductById(product.id.toString()).subscribe({
			next: (persisted) => {
				this.messageAlertService.showMessage(
					'info',
					this.translocoService.translate('productsCrud.messages.editMode'),
					this.translocoService.translate('productsCrud.messages.editing', { name: persisted.name }),
				);
				this.crudProduct.show(persisted);
			},
			error: () =>
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('productsCrud.messages.editNotAvailable'),
				),
		});
	}

	/**
	 * Handles the deletion of one or multiple products.
	 */
	onDeleteProduct(products: ProductTableData | ProductTableData[]): void {
		if (!products || (Array.isArray(products) && products.length === 0)) {
			console.error('No products provided for deletion');
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('productsCrud.messages.noSelection'),
			);
			return;
		}

		const productArray = Array.isArray(products) ? products : [products];
		const target =
			productArray.length === 1
				? this.translocoService.translate('productsCrud.messages.deleteTarget', { name: productArray[0].name })
				: this.translocoService.translate('productsCrud.messages.deleteTargetMany', {
						count: productArray.length,
					});

		this.confirmDialogService.confirmAndExecute(
			'delete',
			target,
			(): Observable<unknown> =>
				productArray.length === 1
					? this.productService.deleteProduct(productArray[0].id.toString())
					: this.productService.bulkDeleteProducts(productArray.map((item) => item.id)), // every selected row in one request
			{
				onSuccess: () => this.loadProducts(),
				errorMessage: this.translocoService.translate('productsCrud.messages.deleteInUse'),
			},
		);
	}

	getMenuActions(product: ProductTableData): TableAction[] {
		return [
			{
				id: 1,
				label: this.translocoService.translate('productsCrud.actions.viewDetail'),
				icon: 'Eye',
				action: 'view',
				command: () => this.onViewDetail(product),
			},
			{
				id: 2,
				label: this.translocoService.translate('productsCrud.actions.edit'),
				icon: 'Edit',
				action: 'update',
				command: () => this.onUpdateProduct(product),
			},
			{
				id: 3,
				label: this.translocoService.translate('productsCrud.actions.delete'),
				icon: 'Trash',
				action: 'delete',
				class: 'red',
				separator: true,
				command: () => this.onDeleteProduct(product),
			},
		];
	}

	menuActionTriggered(event: { action: string; data: ProductTableData | ProductTableData[] }): void {
		if (!event?.action || !event.data) return;

		switch (event.action) {
			case 'view':
				this.onViewDetail(event.data as ProductTableData);
				break;
			case 'delete':
				this.onDeleteProduct(event.data);
				break;
			case 'update':
				this.onUpdateProduct(event.data as ProductTableData);
				break;
			default:
				console.warn('Unknown action:', event.action);
		}
	}

	onRowSelected(product: ProductTableData) {
		this.onViewDetail(product);
	}
}
